const textTransformOptions = [
  { text: 'Default', value: 'default' },
  { text: 'Uppercase', value: 'uppercase' },
  { text: 'Lowercase', value: 'lowercase' },
  { text: 'Capitalize', value: 'capitalize' },
] as const;

const alignmentOptions = [
  { text: 'Left', value: 'left' },
  { text: 'Center', value: 'center' },
  { text: 'Right', value: 'right' },
] as const;

export const ALERT_MODAL_DEFAULT_CLOSE_ASSET =
  'data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M15%205L5%2015M5%205L15%2015%22%20stroke%3D%22%23EFEFEF%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E';

/**
 * Creates a color picker field for the Alert Modal theme subsection.
 */
function colorField(key: string, label: string, initialValue: string) {
  return {
    key,
    label,
    type: 'color_picker_rgba' as const,
    initial_value: initialValue,
  };
}

/**
 * Creates a number input field for the Alert Modal theme subsection.
 */
function numberField(key: string, label: string, initialValue: number) {
  return {
    key,
    label,
    type: 'number_input' as const,
    initial_value: initialValue,
  };
}

/**
 * Creates a font selector field for the Alert Modal theme subsection.
 */
function fontField(key: string, label: string, initialValue: string) {
  return {
    key,
    label,
    type: 'web_font_selector' as const,
    initial_value: initialValue,
  };
}

/**
 * Creates a text-transform select field for the Alert Modal theme subsection.
 */
function textTransformField(key: string, label: string) {
  return {
    key,
    label,
    type: 'select' as const,
    initial_value: 'default',
    options: textTransformOptions,
  };
}

/**
 * Shared Mobile or Desktop message typography and padding fields.
 */
function messageBreakpointFields(breakpoint: 'mobile' | 'desktop') {
  const isMobile = breakpoint === 'mobile';
  const prefix = isMobile ? 'Mobile' : 'Desktop';
  const suffix = `_${breakpoint}`;

  return [
    numberField(
      `alert_modal_message_padding_top${suffix}`,
      `${prefix} Padding Top`,
      isMobile ? 16 : 4
    ),
    numberField(
      `alert_modal_message_padding_right${suffix}`,
      `${prefix} Padding Right`,
      20
    ),
    numberField(
      `alert_modal_message_padding_bottom${suffix}`,
      `${prefix} Padding Bottom`,
      isMobile ? 16 : 4
    ),
    numberField(
      `alert_modal_message_padding_left${suffix}`,
      `${prefix} Padding Left`,
      20
    ),
    colorField(
      `alert_modal_message_font_color${suffix}`,
      `${prefix} Font Color`,
      '#999999'
    ),
    fontField(
      `alert_modal_message_font_family${suffix}`,
      `${prefix} Font Family`,
      'Ubuntu-Regular'
    ),
    numberField(
      `alert_modal_message_font_size${suffix}`,
      `${prefix} Font Size`,
      isMobile ? 16 : 14
    ),
    numberField(
      `alert_modal_message_line_height${suffix}`,
      `${prefix} Line Height`,
      isMobile ? 22 : 20
    ),
    numberField(
      `alert_modal_message_letter_spacing${suffix}`,
      `${prefix} Letter Spacing`,
      -0.1
    ),
    textTransformField(
      `alert_modal_message_text_transform${suffix}`,
      `${prefix} Text Transform`
    ),
  ];
}

/**
 * Shared Mobile or Desktop button layout and typography fields.
 */
function sharedButtonBreakpointFields(breakpoint: 'mobile' | 'desktop') {
  const isMobile = breakpoint === 'mobile';
  const prefix = isMobile ? 'Mobile Button' : 'Desktop Button';
  const suffix = `_${breakpoint}`;

  return [
    numberField(
      `alert_modal_button_corner_radius${suffix}`,
      `${prefix} Corner Radius`,
      12
    ),
    numberField(
      `alert_modal_button_padding_top${suffix}`,
      `${prefix} Padding Top`,
      isMobile ? 14 : 12
    ),
    numberField(
      `alert_modal_button_padding_right${suffix}`,
      `${prefix} Padding Right`,
      isMobile ? 20 : 10
    ),
    numberField(
      `alert_modal_button_padding_bottom${suffix}`,
      `${prefix} Padding Bottom`,
      isMobile ? 14 : 12
    ),
    numberField(
      `alert_modal_button_padding_left${suffix}`,
      `${prefix} Padding Left`,
      isMobile ? 20 : 10
    ),
    fontField(
      `alert_modal_button_font_family${suffix}`,
      `${prefix} Font Family`,
      'Ubuntu-Bold'
    ),
    numberField(
      `alert_modal_button_font_size${suffix}`,
      `${prefix} Font Size`,
      isMobile ? 16 : 14
    ),
    numberField(
      `alert_modal_button_line_height${suffix}`,
      `${prefix} Line Height`,
      isMobile ? 20 : 16
    ),
    numberField(
      `alert_modal_button_letter_spacing${suffix}`,
      `${prefix} Letter Spacing`,
      -0.2
    ),
    textTransformField(
      `alert_modal_button_text_transform${suffix}`,
      `${prefix} Text Transform`
    ),
  ];
}

/**
 * Primary or Secondary button color fields for one breakpoint.
 */
function variantButtonColorFields({
  variant,
  breakpoint,
}: {
  variant: 'primary' | 'secondary';
  breakpoint: 'mobile' | 'desktop';
}) {
  const isPrimary = variant === 'primary';
  const prefix = `${breakpoint === 'mobile' ? 'Mobile' : 'Desktop'} ${
    isPrimary ? 'Primary' : 'Secondary'
  } Button`;
  const suffix = `_${breakpoint}`;
  const keyPrefix = `alert_modal_${variant}_button`;

  return [
    colorField(
      `${keyPrefix}_default_background_color${suffix}`,
      `${prefix} Default Background Color`,
      isPrimary ? '#EFEFEF' : '#3E3E3E'
    ),
    colorField(
      `${keyPrefix}_focus_background_color${suffix}`,
      `${prefix} Focus Background Color`,
      isPrimary ? '#FFFFFF' : '#4E4E4E'
    ),
    colorField(
      `${keyPrefix}_inactive_background_color${suffix}`,
      `${prefix} Inactive Background Color`,
      isPrimary ? '#999999' : '#2E2E2E'
    ),
    colorField(
      `${keyPrefix}_default_font_color${suffix}`,
      `${prefix} Default Font Color`,
      isPrimary ? '#111111' : '#EFEFEF'
    ),
    colorField(
      `${keyPrefix}_focus_font_color${suffix}`,
      `${prefix} Focused Font Color`,
      isPrimary ? '#111111' : '#EFEFEF'
    ),
    colorField(
      `${keyPrefix}_inactive_font_color${suffix}`,
      `${prefix} Inactive Font Color`,
      isPrimary ? '#111111' : 'rgba(239, 239, 239, 0.5)'
    ),
  ];
}

export const alertModalFields = [
  {
    group: true,
    label: 'Alert Modal – General',
    folded: true,
    label_tooltip:
      'This section allows you to configure the general appearance and layout of the Alert Modal, including the modal container, background overlay, and button layout.',
    fields: [
      colorField(
        'alert_modal_background_color',
        'Background Color',
        'rgba(30, 30, 30, 1)'
      ),
      numberField('alert_modal_border_size', 'Border Size', 1),
      colorField(
        'alert_modal_border_color',
        'Border Color',
        'rgba(139, 139, 139, 0.1)'
      ),
      numberField('alert_modal_corner_radius', 'Corner Radius', 20),
      colorField('alert_modal_shadow_color', 'Shadow Color', '#000000'),
      colorField(
        'alert_modal_scrim_color',
        'Scrim Color',
        'rgba(0, 0, 0, 0.6)'
      ),
      {
        key: 'alert_modal_buttons_container_alignment',
        label: 'Buttons Container Alignment',
        type: 'select' as const,
        initial_value: 'right',
        options: alignmentOptions,
      },
      numberField(
        'alert_modal_buttons_container_padding_top',
        'Buttons Container Padding Top',
        20
      ),
      numberField(
        'alert_modal_buttons_container_padding_right',
        'Buttons Container Padding Right',
        20
      ),
      numberField(
        'alert_modal_buttons_container_padding_bottom',
        'Buttons Container Padding Bottom',
        20
      ),
      numberField(
        'alert_modal_buttons_container_padding_left',
        'Buttons Container Padding Left',
        20
      ),
      numberField(
        'alert_modal_buttons_container_horizontal_gutter',
        'Buttons Container Horizontal Gutter',
        8
      ),
    ],
  },
  {
    group: true,
    label: 'Alert Modal – Header',
    folded: true,
    label_tooltip:
      'This section allows you to configure the appearance of the Alert Modal title and close button.',
    fields: [
      colorField('alert_modal_title_font_color', 'Title Font Color', '#EFEFEF'),
      fontField(
        'alert_modal_title_font_family',
        'Title Font Family',
        'Ubuntu-Medium'
      ),
      numberField('alert_modal_title_font_size', 'Title Font Size', 18),
      numberField('alert_modal_title_line_height', 'Title Line Height', 32),
      numberField(
        'alert_modal_title_letter_spacing',
        'Title Letter Spacing',
        -0.2
      ),
      textTransformField(
        'alert_modal_title_text_transform',
        'Title Text Transform'
      ),
      colorField(
        'alert_modal_title_border_bottom_color',
        'Title Border Bottom Color',
        'rgba(0, 0, 0, 0)'
      ),
      {
        key: 'alert_modal_close_button_asset',
        label: 'Close Button Asset',
        type: 'uploader' as const,
        initial_value: ALERT_MODAL_DEFAULT_CLOSE_ASSET,
      },
      colorField(
        'alert_modal_close_button_default_background_color',
        'Close Button Default Background Color',
        'rgba(0, 0, 0, 0)'
      ),
      colorField(
        'alert_modal_close_button_hover_background_color',
        'Close Button Hover Background Color',
        'rgba(46, 46, 46, 1)'
      ),
    ],
  },
  {
    group: true,
    label: 'Alert Modal – Message',
    folded: true,
    label_tooltip:
      'This section allows you to configure the appearance of the Alert Modal message on Mobile Web and Desktop.',
    fields: [
      ...messageBreakpointFields('mobile'),
      ...messageBreakpointFields('desktop'),
    ],
  },
  {
    group: true,
    label: 'Alert Modal – Buttons',
    folded: true,
    label_tooltip:
      'This section allows you to configure the appearance of the Alert Modal buttons on Mobile Web and Desktop, including shared button styling and Primary and Secondary button colors.',
    fields: [
      ...sharedButtonBreakpointFields('mobile'),
      ...variantButtonColorFields({
        variant: 'primary',
        breakpoint: 'mobile',
      }),
      ...variantButtonColorFields({
        variant: 'secondary',
        breakpoint: 'mobile',
      }),
      ...sharedButtonBreakpointFields('desktop'),
      ...variantButtonColorFields({
        variant: 'primary',
        breakpoint: 'desktop',
      }),
      ...variantButtonColorFields({
        variant: 'secondary',
        breakpoint: 'desktop',
      }),
    ],
  },
] as const;
